import type { ReactNode } from 'react';
import { ChevronLeft, ChevronRight, Inbox, Loader2 } from 'lucide-react';
import { cx } from './form.tsx';

type Tone = 'green' | 'gray' | 'amber' | 'coral' | 'teal' | 'sky';
const toneStyles: Record<Tone, string> = {
  green: 'bg-brand/15 text-brand-700',
  gray: 'bg-canvas text-muted ring-1 ring-inset ring-line',
  amber: 'bg-amber/20 text-[#8a5a00]',
  coral: 'bg-coral/15 text-[#b3302c]',
  teal: 'bg-teal/15 text-[#0b7a63]',
  sky: 'bg-sky/25 text-[#1f6f66]',
};

export function Badge({ tone = 'gray', children }: { tone?: Tone; children: ReactNode }) {
  return <span className={cx('inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium', toneStyles[tone])}>{children}</span>;
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-12 text-muted">
      <Loader2 className="size-5 animate-spin" />
      {label && <span className="text-sm">{label}</span>}
    </div>
  );
}

export function EmptyState({ title, description, action }: { title: string; description?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
      <div className="mb-3 rounded-full bg-mint p-3">
        <Inbox className="size-6 text-ink-soft" />
      </div>
      <div className="font-medium text-ink">{title}</div>
      {description && <p className="mt-1 max-w-sm text-sm text-muted">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export interface Column<T> {
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  className?: string;
}

export function Table<T>({ columns, rows, rowKey, onRowClick, empty }: { columns: Column<T>[]; rows: T[]; rowKey: (row: T) => string; onRowClick?: (row: T) => void; empty?: ReactNode }) {
  if (rows.length === 0) return <>{empty ?? <EmptyState title="未有資料" />}</>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[640px] text-left text-[14px]">
        <thead>
          <tr className="border-b border-line bg-canvas/60">
            {columns.map((c) => (
              <th key={c.key} className={cx('px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted', c.className)}>
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={rowKey(row)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={cx('border-b border-line last:border-0', onRowClick && 'cursor-pointer hover:bg-mint/50')}
            >
              {columns.map((c) => (
                <td key={c.key} className={cx('px-4 py-3 align-middle', c.className)}>
                  {c.cell(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Pagination({ page, pageSize, total, onPage }: { page: number; pageSize: number; total: number; onPage: (p: number) => void }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (total <= pageSize) return null;
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3 text-sm text-muted">
      <span>
        第 {page} / {pages} 頁 · 共 {total} 項
      </span>
      <div className="flex gap-1">
        <button type="button" disabled={page <= 1} onClick={() => onPage(page - 1)} className="rounded-full p-2 hover:bg-mint disabled:opacity-40" aria-label="上一頁">
          <ChevronLeft className="size-4" />
        </button>
        <button type="button" disabled={page >= pages} onClick={() => onPage(page + 1)} className="rounded-full p-2 hover:bg-mint disabled:opacity-40" aria-label="下一頁">
          <ChevronRight className="size-4" />
        </button>
      </div>
    </div>
  );
}

/** Underline tabs for switching sections of one page. */
export function Tabs<K extends string>({ tabs, value, onChange }: { tabs: Array<{ key: K; label: ReactNode }>; value: K; onChange: (k: K) => void }) {
  return (
    <div role="tablist" className="flex gap-1 overflow-x-auto border-b border-line">
      {tabs.map((t) => (
        <button
          key={t.key}
          type="button"
          role="tab"
          aria-selected={value === t.key}
          onClick={() => onChange(t.key)}
          className={cx(
            '-mb-px whitespace-nowrap border-b-2 px-4 py-2.5 text-[15px] transition-colors',
            value === t.key ? 'border-brand font-medium text-ink' : 'border-transparent text-muted hover:text-ink',
          )}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}
